import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Calendar as CalendarIcon,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Building2,
  Clock,
  Wrench
} from "lucide-react";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from "date-fns";
import { ar } from "date-fns/locale";

const Schedule = () => {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("month");

  useEffect(() => {
    fetchRequests();
  }, [token]);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/maintenance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      toast.error("فشل في تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  const scheduledRequests = requests.filter(r => r.scheduled_date && r.status !== "completed" && r.status !== "cancelled");

  const getRequestsForDate = (date) => {
    return scheduledRequests.filter(r => 
      isSameDay(new Date(r.scheduled_date), date)
    );
  };

  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  };

  const getPriorityLabel = (priority) => {
    const labels = { low: "منخفضة", medium: "متوسطة", high: "عالية", urgent: "عاجلة" };
    return labels[priority] || priority;
  };

  const getStatusLabel = (status) => {
    const labels = { pending: "قيد الانتظار", scheduled: "مجدول", in_progress: "قيد التنفيذ" };
    return labels[status] || status;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      wall: "الجدران", floor: "الأرضيات", ceiling: "السقف", lighting: "الإضاءة",
      plumbing: "السباكة", electrical: "الكهرباء", dental_chair: "كرسي الأسنان",
      xray: "جهاز الأشعة", sterilizer: "جهاز التعقيم", compressor: "الضاغط",
      suction: "جهاز الشفط", other: "أخرى"
    };
    return labels[category] || category;
  };

  const selectedDateRequests = getRequestsForDate(selectedDate);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="lucent-loader"><img src="/lucent-logo.png" alt="LUCENT" /><div className="lucent-loader-bar"></div></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="schedule-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#655dc6]">جدولة الصيانة</h1>
          <p className="text-slate-500 mt-1">عرض وإدارة مواعيد الصيانة المجدولة</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-32" data-testid="view-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">شهري</SelectItem>
              <SelectItem value="week">أسبوعي</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <CalendarIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#655dc6]">{scheduledRequests.length}</p>
                <p className="text-sm text-slate-500">صيانة مجدولة</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#655dc6]">{selectedDateRequests.length}</p>
                <p className="text-sm text-slate-500">مهام اليوم المحدد</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <Wrench className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#655dc6]">
                  {scheduledRequests.filter(r => r.priority === "urgent" || r.priority === "high").length}
                </p>
                <p className="text-sm text-slate-500">أولوية عالية</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="border-0 shadow-sm lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-[#655dc6]">
                {format(currentMonth, "MMMM yyyy", { locale: ar })}
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  data-testid="prev-month-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  data-testid="next-month-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-1 mb-2">
              {["أحد", "اثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"].map(day => (
                <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {getDaysInMonth().map((date, index) => {
                const dayRequests = getRequestsForDate(date);
                const isSelected = isSameDay(date, selectedDate);
                const isToday = isSameDay(date, new Date());
                const hasUrgent = dayRequests.some(r => r.priority === "urgent" || r.priority === "high");
                
                return (
                  <button
                    key={index}
                    onClick={() => setSelectedDate(date)}
                    className={`
                      aspect-square p-1 rounded-lg relative transition-all
                      ${isSelected ? "bg-[#655dc6] text-white" : "hover:bg-slate-100"}
                      ${isToday && !isSelected ? "ring-2 ring-[#9b8fd4]" : ""}
                    `}
                    data-testid={`calendar-day-${format(date, "yyyy-MM-dd")}`}
                  >
                    <span className="text-sm">{format(date, "d")}</span>
                    {dayRequests.length > 0 && (
                      <div className={`
                        absolute bottom-1 left-1/2 -translate-x-1/2 
                        w-1.5 h-1.5 rounded-full
                        ${hasUrgent ? "bg-red-500" : "bg-[#9b8fd4]"}
                        ${isSelected ? "bg-white" : ""}
                      `} />
                    )}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Selected Date Details */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg text-[#655dc6]">
              {format(selectedDate, "EEEE، d MMMM", { locale: ar })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedDateRequests.length > 0 ? (
              <div className="space-y-3">
                {selectedDateRequests.map((request) => (
                  <div
                    key={request.id}
                    className="p-3 bg-slate-50 rounded-lg border-r-4"
                    style={{
                      borderColor: request.priority === "urgent" ? "#EF4444" :
                        request.priority === "high" ? "#F97316" :
                        request.priority === "medium" ? "#F59E0B" : "#3B82F6"
                    }}
                    data-testid={`schedule-request-${request.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-[#655dc6]" />
                        <span className="font-medium text-[#655dc6] text-sm">{request.clinic_name}</span>
                      </div>
                      <Badge className={`priority-${request.priority} text-xs`}>
                        {getPriorityLabel(request.priority)}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 mb-2">{getCategoryLabel(request.category)}</p>
                    <p className="text-xs text-slate-500 line-clamp-2">{request.description}</p>
                    {request.assigned_to && (
                      <p className="text-xs text-[#9b8fd4] mt-2">المسؤول: {request.assigned_to}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">لا توجد مهام في هذا اليوم</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* All Scheduled */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#655dc6]">جميع الصيانات المجدولة</CardTitle>
        </CardHeader>
        <CardContent>
          {scheduledRequests.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th>العيادة</th>
                    <th>الفئة</th>
                    <th>التاريخ</th>
                    <th>الأولوية</th>
                    <th>الحالة</th>
                    <th>المسؤول</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduledRequests.map((request) => (
                    <tr key={request.id}>
                      <td className="font-medium">{request.clinic_name}</td>
                      <td>{getCategoryLabel(request.category)}</td>
                      <td>{format(new Date(request.scheduled_date), "d MMM yyyy", { locale: ar })}</td>
                      <td>
                        <Badge className={`priority-${request.priority}`}>
                          {getPriorityLabel(request.priority)}
                        </Badge>
                      </td>
                      <td>
                        <Badge className={`status-${request.status}`}>
                          {getStatusLabel(request.status)}
                        </Badge>
                      </td>
                      <td>{request.assigned_to || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">لا توجد صيانات مجدولة</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Schedule;
